'use client';

import { useId, useState, type CSSProperties } from 'react';

import { FlywaysMapIsland } from '@/components/charts/FlywaysMapIsland';
import { OutboundTextLink } from '@/components/patterns/OutboundTextLink';
import { GA_CHART_ID, GA_EVENTS, GA_LINK_PLACEMENT, GA_PARAMS } from '@/constants/analytics';
import {
  FLYWAYS_MAP_COLORS,
  FLYWAYS_MAP_COMPACT_MEDIA_QUERY,
  FLYWAYS_MAP_DEFAULT_FILTER,
  FLYWAYS_MAP_FILTERS,
  FLYWAYS_MAP_LEGEND,
  type FlywaysMapDir,
  type FlywaysMapFilterId,
} from '@/constants/flywaysMap';
import { useMatchMedia } from '@/hooks/useMatchMedia';
import { trackEvent } from '@/lib/analytics/trackEvent';

import styles from './FlywaysMapFrame.module.css';
import {
  FlywaysMapMobileNav,
  FlywaysMapMobileOverlays,
} from './FlywaysMapMobileChrome';

import type { Messages } from '@/content/messages';

type FlywaysMapChromeProps = {
  messages: Messages['intro']['flywaysMap'];
  mapCaption: string;
  dir: FlywaysMapDir;
};

/**
 * Client chrome for Migration routes: owns flyway filter state, legend-only
 * subrows, and the map island (fetch-into-plate). Below `lg`, swaps to compact
 * carousel + overlays; `lg+` keeps desktop tabs.
 */
export function FlywaysMapChrome({
  messages,
  mapCaption,
  dir,
}: FlywaysMapChromeProps) {
  const [flywayFilter, setFlywayFilter] = useState<FlywaysMapFilterId>(
    FLYWAYS_MAP_DEFAULT_FILTER,
  );

  function handleFlywayFilterChange(next: FlywaysMapFilterId) {
    setFlywayFilter(next);
    trackEvent(GA_EVENTS.chartFilter, {
      [GA_PARAMS.chartId]: GA_CHART_ID.flywaysMap,
      [GA_PARAMS.flywayId]: next,
    });
  }

  const liveId = useId();
  const compact = useMatchMedia(FLYWAYS_MAP_COMPACT_MEDIA_QUERY);

  const activeLabel =
    messages.filters[FLYWAYS_MAP_FILTERS.indexOf(flywayFilter)] ??
    messages.filters[0];
  const announcement = messages.activeFlywayAnnouncement.replace(
    '{flyway}',
    activeLabel,
  );

  return (
    <>
      <div className={styles.compactNav}>
        <FlywaysMapMobileNav
          filter={flywayFilter}
          onFilterChange={handleFlywayFilterChange}
          messages={messages}
        />
      </div>

      <div className={styles.filters}>
        <h3 className={styles.filterHeading}>{messages.heading}</h3>

        <div className={styles.filterGroup}>
          <p className={styles.filterLabel}>{messages.filterByLabel}</p>
          <ul
            className={styles.filterList}
            role="group"
            aria-label={messages.filterByLabel}
          >
            {FLYWAYS_MAP_FILTERS.map((filterId, index) => {
              const label = messages.filters[index] ?? filterId;
              const isActive = flywayFilter === filterId;
              const hasLegend = filterId !== 'all';
              const flywayColor = hasLegend
                ? FLYWAYS_MAP_COLORS[filterId]
                : undefined;
              const legendItems = hasLegend
                ? FLYWAYS_MAP_LEGEND[filterId]
                : null;

              return (
                <li
                  key={filterId}
                  className={
                    isActive && hasLegend ? styles.filterCluster : undefined
                  }
                >
                  <button
                    type="button"
                    className={
                      isActive ? styles.filterTabActive : styles.filterTab
                    }
                    aria-pressed={isActive}
                    data-flyway-filter={filterId}
                    style={
                      isActive && flywayColor
                        ? {
                            backgroundColor: 'var(--color-ink)',
                            color: flywayColor,
                          }
                        : undefined
                    }
                    onClick={() => handleFlywayFilterChange(filterId)}
                  >
                    {label}
                  </button>

                  {hasLegend && legendItems && flywayColor ? (
                    <div
                      className={
                        isActive
                          ? `${styles.legendClip} ${styles.legendClipOpen}`
                          : styles.legendClip
                      }
                      aria-hidden={!isActive}
                    >
                      <div className={styles.legendClipInner}>
                        <ul className={styles.legendList} aria-label={label}>
                          {legendItems.map((item) => {
                            const itemLabel =
                              messages.legendItems[item.labelKey];
                            return (
                              <li key={item.id} className={styles.legendItem}>
                                <span
                                  className={styles.legendSwatch}
                                  data-pattern={item.pattern}
                                  style={
                                    {
                                      '--legend-color': flywayColor,
                                    } as CSSProperties
                                  }
                                  aria-hidden
                                />
                                <span className={styles.legendLabel}>
                                  {itemLabel}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>

        {flywayFilter !== FLYWAYS_MAP_DEFAULT_FILTER ? (
          <button
            type="button"
            className={styles.clearSelection}
            onClick={() => handleFlywayFilterChange(FLYWAYS_MAP_DEFAULT_FILTER)}
          >
            {messages.clearSelection}
          </button>
        ) : null}

        <OutboundTextLink
          href={messages.datazoneUrl}
          className={styles.datazoneLink}
          linkPlacement={GA_LINK_PLACEMENT.datazoneChart}
        >
          {messages.datazoneLink}
        </OutboundTextLink>
      </div>

      <div className={styles.mapColumn}>
        <div className={styles.mapPlate}>
          <FlywaysMapIsland
            filter={flywayFilter}
            messages={messages}
            caption={mapCaption}
            dir={dir}
            compact={compact}
          />
          <div className={styles.compactOverlays}>
            <FlywaysMapMobileOverlays
              key={flywayFilter}
              filter={flywayFilter}
              messages={messages}
            />
          </div>
        </div>
        <p id={liveId} className={styles.liveRegion} aria-live="polite">
          {announcement}
        </p>
      </div>
    </>
  );
}
